// pages/HouseProfilePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Badge,
  Container,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  fetchHouseProfileThunk,
  clearHouseProfileError,
} from "../../../store/slices/houseProfileSlice";

import {
  createHouseConnectionRequestThunk,
  fetchHouseConnectionHistoryThunk,
  clearHouseConnectionError,
  clearHouseConnectionAction,
} from "../../../store/slices/houseConnectionSlice";

import SummaryTab from "./tabs/SummaryTab";
import GeneralDetailsTab from "./tabs/GeneralDetailsTab";
import OldBillsTab from "./tabs/OldBillsTab";
import OldSlrmBillsTab from "./tabs/OldSlrmBillsTab";
import ReceiptsTab from "./tabs/ReceiptsTab";
import SlrmReceiptsTab from "./tabs/SlrmReceiptsTab";
import DiscountsTab from "./tabs/DiscountsTab";
import MeterResetTab from "./tabs/MeterResetTab";
import InitialSettingsTab from "./tabs/InitialSettingsTab";
import OtherSettingsTab from "./tabs/OtherSettingsTab";

import "../../../css/master-common.css";

// ============================================================
// SMALL HELPERS
// ============================================================

const n0 = (v) => Number(v || 0);

const money = (v) =>
  `₹${Math.round(n0(v)).toLocaleString("en-IN")}`;

const safe = (v, dash = "-") =>
  v === null || v === undefined || v === "" ? dash : v;

const yn = (v) =>
  String(v || "").toLowerCase() === "yes" ? "Yes" : "No";

const formatDate = (date) => {
  if (!date || date === "N/A" || date === "-" || date === null) {
    return "N/A";
  }
  return date;
};

const todayYMD = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// ============================================================
// CHIP
// ============================================================

const Chip = ({ type = "water", children }) => {
  let bgColor = "#f0f7ff";
  let textColor = "#0066cc";
  let borderColor = "#cce4ff";

  if (type === "water" || type === "together") {
    bgColor = "#e6f2ff";
    borderColor = "#b8d9ff";
  } else if (type === "slrm") {
    bgColor = "#f0f7ff";
    borderColor = "#cce4ff";
  } else if (type === "pending") {
    bgColor = "#fff7ed";
    textColor = "#c2410c";
    borderColor = "#fed7aa";
  } else if (type === "ok") {
    bgColor = "#e8f5e9";
    textColor = "#28a745";
    borderColor = "#c8e6c9";
  } else if (type === "disconnected") {
    bgColor = "#fff1f0";
    textColor = "#dc3545";
    borderColor = "#ffcdd2";
  }

  return (
    <span
      style={{
        backgroundColor: bgColor,
        color: textColor,
        border: `1px solid ${borderColor}`,
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "13px",
        fontWeight: "500",
        display: "inline-block",
      }}
    >
      {children}
    </span>
  );
};

// ============================================================
// STATS
// ============================================================

const StatCard = ({ title, value, sub, highlight = false }) => (
  <Card
    style={{
      border: "1px solid #e9ecef",
      borderRadius: "12px",
      height: "100%",
    }}
  >
    <Card.Body style={{ padding: "16px" }}>
      <div
        style={{
          fontSize: "13px",
          color: "#6b7280",
          marginBottom: "6px",
          textTransform: "uppercase",
          letterSpacing: "0.3px",
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontSize: "24px",
          fontWeight: "600",
          color: highlight ? "#0066cc" : "#111827",
          marginBottom: "4px",
        }}
      >
        {value}
      </div>

      {sub && (
        <div style={{ fontSize: "12px", color: "#9ca3af" }}>
          {sub}
        </div>
      )}
    </Card.Body>
  </Card>
);

const MiniStat = ({ label, value, highlight = false }) => (
  <div
    style={{
      backgroundColor: "#f9fafb",
      padding: "12px",
      borderRadius: "10px",
      border: "1px solid #f0f2f5",
    }}
  >
    <div
      style={{
        fontSize: "12px",
        color: "#6b7280",
        marginBottom: "4px",
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontSize: "16px",
        fontWeight: "600",
        color: highlight ? "#0066cc" : "#111827",
      }}
    >
      {value}
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      padding: "8px 0",
      borderBottom: "1px solid #f0f2f5",
    }}
  >
    <div
      style={{
        width: "145px",
        fontSize: "13px",
        color: "#6b7280",
        flexShrink: 0,
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontSize: "13px",
        color: "#111827",
        fontWeight: "500",
      }}
    >
      {value}
    </div>
  </div>
);

// ============================================================
// PAGE
// ============================================================

export default function HouseProfilePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { home_id } = useParams();

  const pHouse =
    useSelector((s) => s.permissions?.map?.house_master) || {};

  const canView = pHouse.can_view !== false;
  const canUpdate = pHouse.can_update !== false;

  const { data, loading, error } = useSelector(
    (s) => s.houseProfile
  );

  const [activeTab, setActiveTab] = useState("summary");

  // ==========================================================
  // WATER CONNECTION MODAL STATE
  // ==========================================================

  const [connectionModal, setConnectionModal] = useState({
    show: false,
    actionType: "disconnection",
  });

  const [connectionForm, setConnectionForm] = useState({
    reason: "",
    action_date: todayYMD(),
    current_reading: "",
    charge_amount: "0",
    remarks: "",
  });

  const {
    history: connectionHistory,
    historyLoading: connectionHistoryLoading,
    submitting: submittingConnection,
    error: connectionError,
    actionSuccess: connectionActionSuccess,
    actionMessage: connectionActionMessage,
  } = useSelector((s) => s.houseConnection);

  // ==========================================================
  // PROFILE NORMALIZATION
  // ==========================================================

  const profile = useMemo(() => {
    if (!data) return null;
    return data?.house ? data : data?.data;
  }, [data]);

  const house = profile?.house || null;
  const summary = profile?.summary || {};
  const metrics = profile?.metrics || {};
  const mDCB = metrics?.dcb || {};
  const mPending = metrics?.pending || {};

  const isWater =
    String(house?.is_water || "").toLowerCase() === "yes";

  const isSlrm =
    String(house?.is_slrm || "").toLowerCase() === "yes";

  const waterConnectionStatus = String(
    house?.water_connection_status || "Active"
  ).toLowerCase();

  const waterDisconnected =
    isWater && waterConnectionStatus === "disconnected";

  const waterActive = isWater && !waterDisconnected;

  const totalPaid = n0(summary?.total_paid);
  const waterPaid = n0(summary?.water_paid);
  const slrmPaid = n0(summary?.slrm_paid);

  // ==========================================================
  // IMPORTANT:
  // NO OUTSTANDING CALCULATION IN FRONTEND.
  // Backend calculates:
  // water_balance + slrm_bal + connection_balance
  // and returns summary.total_balance.
  // ==========================================================
  const outstanding = n0(summary?.total_balance);

  // These are also backend-calculated values.
  const demand = n0(mDCB?.demand);
  const collection = n0(mDCB?.collection);
  const balance = n0(mDCB?.balance);

  const statusOk =
    String(house?.status || "").toLowerCase() === "active";

  const waterPending =
    isWater && mPending?.water_pending === true;

  const slrmPending =
    isSlrm && mPending?.slrm_pending === true;

  const connectionPending =
    mPending?.connection_pending === true;

  const anyPending =
    mPending?.any_pending === true;

  // ==========================================================
  // PENDING WATER CONNECTION REQUEST
  // ==========================================================

  const pendingConnectionRequest = useMemo(() => {
    const rows = Array.isArray(connectionHistory)
      ? connectionHistory
      : [];

    return (
      rows.find(
        (row) =>
          String(row?.approval_status || "")
            .trim()
            .toLowerCase() === "pending"
      ) || null
    );
  }, [connectionHistory]);

  const pendingConnectionAction = String(
    pendingConnectionRequest?.action_type || ""
  )
    .trim()
    .toLowerCase();

  const pendingDisconnection =
    pendingConnectionAction === "disconnection";

  const pendingReconnection =
    pendingConnectionAction === "reconnection";

  // ==========================================================
  // HOUSE CONNECTION TOASTS
  // ==========================================================

  useEffect(() => {
    if (!connectionActionSuccess) return;

    toast.success(
      connectionActionMessage ||
        "Connection request submitted successfully"
    );

    dispatch(clearHouseConnectionAction());

    if (home_id) {
      dispatch(fetchHouseProfileThunk(home_id));
      dispatch(fetchHouseConnectionHistoryThunk(home_id));
    }
  }, [
    connectionActionSuccess,
    connectionActionMessage,
    dispatch,
    home_id,
  ]);

  useEffect(() => {
    if (!connectionError) return;

    toast.error(String(connectionError));
    dispatch(clearHouseConnectionError());
  }, [connectionError, dispatch]);

  // ==========================================================
  // LOAD PROFILE
  // ==========================================================

  const refreshProfile = () => {
    if (!home_id || !canView) return;

    dispatch(fetchHouseProfileThunk(home_id));
    dispatch(fetchHouseConnectionHistoryThunk(home_id));
  };

  useEffect(() => {
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home_id, canView, dispatch]);

  // ==========================================================
  // REPORT NAVIGATION
  // ==========================================================

  const handleViewDcb = () => {
    if (!home_id) return;

    navigate(
      `/reports/water-dcb?home_id=${encodeURIComponent(home_id)}`
    );
  };

  const handleViewSlrmDcb = () => {
    if (!home_id) return;

    navigate(
      `/reports/slrm-dcb?home_id=${encodeURIComponent(home_id)}`
    );
  };

  // ==========================================================
  // OPEN WATER CONNECTION MODALS
  // ==========================================================

  const openConnectionModal = (actionType) => {
    if (!isWater) {
      toast.error(
        "Water connection is not enabled for this house"
      );
      return;
    }

    if (!canUpdate) {
      toast.error(
        "You do not have permission to perform this action"
      );
      return;
    }

    if (pendingConnectionRequest) {
      toast.info(
        pendingReconnection
          ? "Reconnection request is already pending approval"
          : pendingDisconnection
          ? "Disconnection request is already pending approval"
          : "A Water connection request is already pending approval"
      );
      return;
    }

    const isDisconnection = actionType === "disconnection";

    setConnectionForm({
      reason: "",
      action_date: todayYMD(),
      current_reading: isDisconnection
        ? String(house?.last_reading ?? "")
        : "",
      charge_amount: "0",
      remarks: "",
    });

    setConnectionModal({
      show: true,
      actionType,
    });
  };

  const closeConnectionModal = () => {
    if (submittingConnection) return;

    setConnectionModal({
      show: false,
      actionType: "disconnection",
    });
  };

  const handleConnectionField = (field, value) => {
    setConnectionForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ==========================================================
  // SUBMIT WATER DISCONNECTION / RECONNECTION
  // ==========================================================

  const submitConnectionRequest = async () => {
    if (!home_id) {
      toast.error("House ID is missing");
      return;
    }

    if (pendingConnectionRequest) {
      toast.info(
        pendingReconnection
          ? "Reconnection request is already pending approval"
          : pendingDisconnection
          ? "Disconnection request is already pending approval"
          : "A Water connection request is already pending approval"
      );
      return;
    }

    const actionType = connectionModal.actionType;
    const isDisconnection = actionType === "disconnection";

    if (!connectionForm.action_date) {
      toast.error(
        `${
          isDisconnection ? "Disconnection" : "Reconnection"
        } date is required`
      );
      return;
    }

    if (
      isDisconnection &&
      !connectionForm.reason.trim()
    ) {
      toast.error(
        "Reason is required for Water disconnection"
      );
      return;
    }

    if (
      isDisconnection &&
      String(connectionForm.current_reading).trim() === ""
    ) {
      toast.error("Current meter reading is required");
      return;
    }

    const chargeAmount = Number(
      connectionForm.charge_amount || 0
    );

    if (
      !Number.isFinite(chargeAmount) ||
      chargeAmount < 0
    ) {
      toast.error(
        "Charge amount must be a valid non-negative number"
      );
      return;
    }

    let currentReading = null;

    if (
      String(connectionForm.current_reading).trim() !== ""
    ) {
      currentReading = Number(
        connectionForm.current_reading
      );

      if (
        !Number.isFinite(currentReading) ||
        currentReading < 0
      ) {
        toast.error(
          "Current meter reading must be a valid non-negative number"
        );
        return;
      }

      const lastReading = Number(
        house?.last_reading || 0
      );

      if (
        Number.isFinite(lastReading) &&
        currentReading < lastReading
      ) {
        toast.error(
          `Current meter reading cannot be less than last reading (${lastReading})`
        );
        return;
      }
    }

    const payload = {
      action_type: actionType,
      action_date: connectionForm.action_date,
      reason: isDisconnection
        ? connectionForm.reason.trim()
        : null,
      current_reading: currentReading,
      charge_amount: chargeAmount,
      remarks:
        connectionForm.remarks.trim() || null,
    };

    const housePanId = Number(
      house?.pan_id || 0
    );

    if (!housePanId) {
      toast.error("House Panchayath ID is missing");
      return;
    }

    const result = await dispatch(
      createHouseConnectionRequestThunk({
        home_id,
        pan_id: housePanId,
        payload,
      })
    );

    if (
      createHouseConnectionRequestThunk.rejected.match(
        result
      )
    ) {
      return;
    }

    setConnectionModal({
      show: false,
      actionType: "disconnection",
    });

    refreshProfile();
  };

  // ==========================================================
  // TABS
  // ==========================================================

  const tabs = [
    { key: "summary", label: "Summary" },
    {
      key: "general_details",
      label: "General Details",
    },
    { key: "old_bills", label: "Water Bills" },
    {
      key: "slrm_old_bills",
      label: "SLRM Bills",
    },
    {
      key: "water_receipts",
      label: "Water Receipts",
    },
    {
      key: "slrm_receipts",
      label: "SLRM Receipts",
    },
    { key: "discounts", label: "Discounts" },
    { key: "reset", label: "Meter Reset" },
    {
      key: "settings",
      label: "Initial Setting",
    },
  ];

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        padding: 0,
      }}
    >
      {/* HEADER */}
      <div
        style={{
          borderBottom: "3px solid #0066cc",
          padding: "16px 24px",
          backgroundColor: "#ffffff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              {safe(
                house?.pan_name,
                "House Profile"
              )}
            </h2>

            <p
              style={{
                margin: "4px 0 0",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              House Profile • ID:{" "}
              {safe(house?.home_id)} • Added:{" "}
              {safe(house?.added)}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {isWater && (
              <Chip
                type={
                  waterDisconnected
                    ? "disconnected"
                    : "ok"
                }
              >
                Water:{" "}
                {waterDisconnected
                  ? "Disconnected"
                  : "Active"}
              </Chip>
            )}

            <Badge
              bg="transparent"
              style={{
                backgroundColor: statusOk
                  ? "#eff6ff"
                  : "#f9fafb",
                color: statusOk
                  ? "#2563eb"
                  : "#6b7280",
                border: `1px solid ${
                  statusOk
                    ? "#bfdbfe"
                    : "#e5e7eb"
                }`,
                padding: "6px 14px",
                borderRadius: "999px",
                fontWeight: 500,
              }}
            >
              House: {house?.status || "Inactive"}
            </Badge>
          </div>
        </div>
      </div>

      <div style={{ padding: "24px" }}>
        {error && (
          <Alert
            variant="danger"
            onClose={() =>
              dispatch(
                clearHouseProfileError()
              )
            }
            dismissible
          >
            {error}
          </Alert>
        )}

        {loading && !house ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px",
            }}
          >
            <Spinner
              animation="border"
              variant="primary"
            />

            <p
              style={{
                marginTop: "16px",
                color: "#6b7280",
              }}
            >
              Loading house profile...
            </p>
          </div>
        ) : (
          <>
            {/* HOUSE DETAILS */}
            <Card
              style={{
                border: "1px solid #e9ecef",
                borderRadius: "12px",
                marginBottom: "24px",
              }}
            >
              <Card.Body style={{ padding: "20px" }}>
                <Row>
                  <Col lg={8}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "20px",
                        flexWrap: "wrap",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          margin: 0,
                          color: "#111827",
                        }}
                      >
                        {safe(
                          house?.family_head
                        )}
                      </h3>

                      {isWater ? (
                        <Chip
                          type={
                            waterDisconnected
                              ? "disconnected"
                              : "water"
                          }
                        >
                          Water:{" "}
                          {waterDisconnected
                            ? "Disconnected"
                            : "Active"}
                        </Chip>
                      ) : (
                        <Chip type="water">
                          Water: No
                        </Chip>
                      )}

                      {isSlrm ? (
                        <Chip type="slrm">
                          SLRM: Yes
                        </Chip>
                      ) : (
                        <Chip type="slrm">
                          SLRM: No
                        </Chip>
                      )}

                      <Chip type="together">
                        Bill Together:{" "}
                        {yn(
                          house?.slrm_water_together
                        )}
                      </Chip>

                      {anyPending && (
                        <Chip type="pending">
                          Pending Dues
                        </Chip>
                      )}

                      {connectionPending && (
                        <Chip type="pending">
                          Connection Charge Pending
                        </Chip>
                      )}

                      {pendingConnectionRequest && (
                        <Chip type="pending">
                          {pendingReconnection
                            ? "Reconnection Pending"
                            : pendingDisconnection
                            ? "Disconnection Pending"
                            : "Connection Request Pending"}
                        </Chip>
                      )}

                      <Chip type="together">
                        Receipt Type:{" "}
                        {String(
                          house?.receipt_type ||
                            "separate"
                        ).toLowerCase() ===
                        "together"
                          ? "Together"
                          : "Separate"}
                      </Chip>
                    </div>

                    <Row>
                      <Col md={6}>
                        <InfoRow
                          label="Door No"
                          value={safe(
                            house?.door_no
                          )}
                        />
                        <InfoRow
                          label="QR Code"
                          value={safe(
                            house?.qr_code
                          )}
                        />
                        <InfoRow
                          label="Mobile"
                          value={safe(
                            house?.mobile
                          )}
                        />
                        <InfoRow
                          label="Ward"
                          value={`${safe(
                            house?.ward_name
                          )} (${safe(
                            house?.ward_id
                          )})`}
                        />
                        <InfoRow
                          label="Water Rate"
                          value={safe(
                            house?.meter_type_name
                          )}
                        />
                        <InfoRow
                          label="SLRM Rate"
                          value={safe(
                            house?.slrm_type_name
                          )}
                        />
                        <InfoRow
                          label="Water Status"
                          value={
                            !isWater
                              ? "Not Enabled"
                              : waterDisconnected
                              ? "Disconnected"
                              : "Active"
                          }
                        />

                        {waterDisconnected && (
                          <InfoRow
                            label="Disconnected On"
                            value={formatDate(
                              house?.disconnect_date
                            )}
                          />
                        )}
                      </Col>

                      <Col md={6}>
                        <InfoRow
                          label="Last Water Bill"
                          value={formatDate(
                            house?.last_bill_date
                          )}
                        />
                        <InfoRow
                          label="Last SLRM Bill"
                          value={formatDate(
                            house?.slrm_last_bill_date
                          )}
                        />
                        <InfoRow
                          label="Last Payment"
                          value={
                            formatDate(
                              house?.last_payment_date
                            ) ||
                            "No payments yet"
                          }
                        />
                        <InfoRow
                          label="Last Reading"
                          value={safe(
                            house?.last_reading
                          )}
                        />
                        <InfoRow
                          label="Opening Balance"
                          value={money(
                            house?.op_balance
                          )}
                        />
                        <InfoRow
                          label="Water Usage"
                          value={safe(
                            house?.water_usage,
                            "Not recorded"
                          )}
                        />
                        <InfoRow
                          label="Connection Balance"
                          value={money(
                            summary?.connection_balance
                          )}
                        />

                        {waterActive &&
                          house?.reconnect_date && (
                            <InfoRow
                              label="Reconnected On"
                              value={formatDate(
                                house?.reconnect_date
                              )}
                            />
                          )}
                      </Col>
                    </Row>

                    <div
                      style={{
                        marginTop: "16px",
                        padding: "12px",
                        backgroundColor: "#f9fafb",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          Panchayath:{" "}
                          <strong
                            style={{
                              color: "#111827",
                            }}
                          >
                            {safe(
                              house?.pan_name
                            )}
                          </strong>
                        </span>

                        <span
                          style={{
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          Taluk:{" "}
                          <strong
                            style={{
                              color: "#111827",
                            }}
                          >
                            {safe(
                              house?.taluk_name
                            )}
                          </strong>
                        </span>

                        <span
                          style={{
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          District:{" "}
                          <strong
                            style={{
                              color: "#111827",
                            }}
                          >
                            {safe(
                              house?.dist_name
                            )}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </Col>

                  {/* QUICK ACTIONS */}
                  <Col lg={4}>
                    <div
                      style={{
                        backgroundColor: "#f9fafb",
                        padding: "16px",
                        borderRadius: "12px",
                        height: "100%",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          margin: "0 0 16px",
                          color: "#111827",
                        }}
                      >
                        Quick Actions
                      </h4>

                      {(() => {
                        const mediumBtn = {
                          width: "260px",
                          maxWidth: "100%",
                          margin: "0 auto 12px",
                          padding: "10px",
                          fontSize: "14px",
                          borderRadius: "8px",
                          display: "block",
                        };

                        return (
                          <>
                            {isWater &&
                              pendingConnectionRequest && (
                                <Button
                                  variant="warning"
                                  style={{
                                    ...mediumBtn,
                                    cursor:
                                      "not-allowed",
                                  }}
                                  disabled
                                >
                                  {pendingReconnection
                                    ? "Reconnection Pending"
                                    : pendingDisconnection
                                    ? "Disconnection Pending"
                                    : "Connection Request Pending"}
                                </Button>
                              )}

                            {isWater &&
                              !pendingConnectionRequest &&
                              waterActive && (
                                <Button
                                  variant="danger"
                                  style={mediumBtn}
                                  disabled={
                                    !canUpdate ||
                                    submittingConnection ||
                                    connectionHistoryLoading
                                  }
                                  onClick={() =>
                                    openConnectionModal(
                                      "disconnection"
                                    )
                                  }
                                >
                                  Disconnect Water
                                </Button>
                              )}

                            {isWater &&
                              !pendingConnectionRequest &&
                              waterDisconnected && (
                                <Button
                                  variant="success"
                                  style={mediumBtn}
                                  disabled={
                                    !canUpdate ||
                                    submittingConnection ||
                                    connectionHistoryLoading
                                  }
                                  onClick={() =>
                                    openConnectionModal(
                                      "reconnection"
                                    )
                                  }
                                >
                                  Reconnect Water
                                </Button>
                              )}

                            <Button
                              variant="primary"
                              style={{
                                ...mediumBtn,
                                backgroundColor:
                                  "#0066cc",
                                border: "none",
                              }}
                              onClick={handleViewDcb}
                            >
                              View Detailed Water DCB
                              Report
                            </Button>

                            <Button
                              variant="primary"
                              style={{
                                ...mediumBtn,
                                backgroundColor:
                                  "#0066cc",
                                border: "none",
                              }}
                              onClick={
                                handleViewSlrmDcb
                              }
                            >
                              View Detailed SLRM DCB
                              Report
                            </Button>
                          </>
                        );
                      })()}

                      <div
                        style={{
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "8px",
                          }}
                        >
                          Payment Status
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {isWater && (
                            <Chip
                              type={
                                waterPending
                                  ? "pending"
                                  : "ok"
                              }
                            >
                              Water Bill:{" "}
                              {waterPending
                                ? "Pending"
                                : "Cleared"}
                            </Chip>
                          )}

                          {isSlrm && (
                            <Chip
                              type={
                                slrmPending
                                  ? "pending"
                                  : "ok"
                              }
                            >
                              SLRM Bill:{" "}
                              {slrmPending
                                ? "Pending"
                                : "Cleared"}
                            </Chip>
                          )}

                          {connectionPending && (
                            <Chip type="pending">
                              Connection Charge: Pending
                            </Chip>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                        }}
                      >
                        Outstanding:{" "}
                        {money(outstanding)}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* SUMMARY */}
            <Row className="g-3 mb-4">
              <Col xs={12} lg={6}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: 10,
                  }}
                >
                  Water Summary
                </div>

                <Row className="g-3">
                  <Col xs={12} sm={6}>
                    <StatCard
                      title="WATER PAID"
                      value={money(waterPaid)}
                      sub="Total collection (Water)"
                      highlight={waterPaid > 0}
                    />
                  </Col>

                  <Col xs={12} sm={6}>
                    <StatCard
                      title="WATER BALANCE"
                      value={money(
                        house?.water_balance
                      )}
                      sub="Current balance (Water)"
                      highlight={
                        n0(
                          house?.water_balance
                        ) > 0
                      }
                    />
                  </Col>
                </Row>
              </Col>

              <Col xs={12} lg={6}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: 10,
                  }}
                >
                  SLRM Summary
                </div>

                <Row className="g-3">
                  <Col xs={12} sm={6}>
                    <StatCard
                      title="SLRM PAID"
                      value={money(slrmPaid)}
                      sub="Total collection (SLRM)"
                      highlight={slrmPaid > 0}
                    />
                  </Col>

                  <Col xs={12} sm={6}>
                    <StatCard
                      title="SLRM BALANCE"
                      value={money(
                        house?.slrm_bal
                      )}
                      sub="Current balance (SLRM)"
                      highlight={
                        n0(house?.slrm_bal) > 0
                      }
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col xs={12} md={3}>
                <MiniStat
                  label="Total Demand"
                  value={money(demand)}
                />
              </Col>

              <Col xs={12} md={3}>
                <MiniStat
                  label="Total Collection"
                  value={money(collection)}
                />
              </Col>

              <Col xs={12} md={3}>
                <MiniStat
                  label="Connection Balance"
                  value={money(
                    summary?.connection_balance
                  )}
                  highlight={
                    n0(
                      summary?.connection_balance
                    ) > 0
                  }
                />
              </Col>

              <Col xs={12} md={3}>
                <MiniStat
                  label="Balance Due"
                  value={money(balance)}
                  highlight={balance > 0}
                />
              </Col>
            </Row>

            {/* TABS */}
            <Card
              style={{
                border: "1px solid #e9ecef",
                borderRadius: "12px",
              }}
            >
              <Card.Header
                style={{
                  backgroundColor:
                    "transparent",
                  borderBottom:
                    "1px solid #e9ecef",
                  padding: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    padding: "0 16px",
                  }}
                >
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() =>
                        setActiveTab(tab.key)
                      }
                      style={{
                        padding: "16px 20px",
                        border: "none",
                        background: "none",
                        fontSize: "14px",
                        fontWeight:
                          activeTab === tab.key
                            ? "600"
                            : "500",
                        color:
                          activeTab === tab.key
                            ? "#0066cc"
                            : "#6b7280",
                        borderBottom:
                          activeTab === tab.key
                            ? "2px solid #0066cc"
                            : "2px solid transparent",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </Card.Header>

              <Card.Body style={{ padding: "24px" }}>
                {activeTab === "summary" && (
                  <SummaryTab
                    house={house}
                    summary={summary}
                    metrics={metrics}
                    activeTab={activeTab}
                  />
                )}

                {activeTab ===
                  "general_details" && (
                  <GeneralDetailsTab
                    house={house}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab === "old_bills" && (
                  <OldBillsTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab ===
                  "slrm_old_bills" && (
                  <OldSlrmBillsTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab ===
                  "water_receipts" && (
                  <ReceiptsTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab ===
                  "slrm_receipts" && (
                  <SlrmReceiptsTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab === "discounts" && (
                  <DiscountsTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab === "reset" && (
                  <MeterResetTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab === "settings" && (
                  <InitialSettingsTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}

                {activeTab ===
                  "other_settings" && (
                  <OtherSettingsTab
                    home_id={home_id}
                    activeTab={activeTab}
                    onRefreshProfile={
                      refreshProfile
                    }
                  />
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </div>

      {/* ======================================================
          WATER DISCONNECTION / RECONNECTION MODAL
      ====================================================== */}

      <Modal
        show={connectionModal.show}
        onHide={closeConnectionModal}
        centered
        backdrop={
          submittingConnection
            ? "static"
            : true
        }
      >
        <Modal.Header
          closeButton={!submittingConnection}
        >
          <Modal.Title
            style={{
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            {connectionModal.actionType ===
            "disconnection"
              ? "Disconnect Water Connection"
              : "Reconnect Water Connection"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {safe(house?.family_head)}
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginTop: "4px",
              }}
            >
              Door No: {safe(house?.door_no)} •
              Last Reading:{" "}
              {safe(house?.last_reading)}
            </div>
          </div>

          {connectionModal.actionType ===
            "disconnection" && (
            <Form.Group className="mb-3">
              <Form.Label
                style={{ fontWeight: 500 }}
              >
                Reason{" "}
                <span
                  style={{ color: "#dc3545" }}
                >
                  *
                </span>
              </Form.Label>

              <Form.Control
                type="text"
                placeholder="Enter disconnection reason"
                value={connectionForm.reason}
                disabled={submittingConnection}
                onChange={(e) =>
                  handleConnectionField(
                    "reason",
                    e.target.value
                  )
                }
              />
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label
              style={{ fontWeight: 500 }}
            >
              {connectionModal.actionType ===
              "disconnection"
                ? "Disconnection Date"
                : "Reconnection Date"}{" "}
              <span
                style={{ color: "#dc3545" }}
              >
                *
              </span>
            </Form.Label>

            <Form.Control
              type="date"
              value={
                connectionForm.action_date
              }
              disabled={submittingConnection}
              onChange={(e) =>
                handleConnectionField(
                  "action_date",
                  e.target.value
                )
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label
              style={{ fontWeight: 500 }}
            >
              Current Meter Reading{" "}
              {connectionModal.actionType ===
                "disconnection" && (
                <span
                  style={{
                    color: "#dc3545",
                  }}
                >
                  *
                </span>
              )}
            </Form.Label>

            <Form.Control
              type="number"
              min={n0(house?.last_reading)}
              step="any"
              placeholder={
                connectionModal.actionType ===
                "disconnection"
                  ? "Enter current meter reading"
                  : "Optional starting meter reading"
              }
              value={
                connectionForm.current_reading
              }
              disabled={submittingConnection}
              onChange={(e) =>
                handleConnectionField(
                  "current_reading",
                  e.target.value
                )
              }
            />

            <Form.Text className="text-muted">
              Last saved reading:{" "}
              {safe(
                house?.last_reading,
                "0"
              )}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label
              style={{ fontWeight: 500 }}
            >
              {connectionModal.actionType ===
              "disconnection"
                ? "Disconnection Charge"
                : "Reconnection Charge"}
            </Form.Label>

            <Form.Control
              type="number"
              min="0"
              step="1"
              value={
                connectionForm.charge_amount
              }
              disabled={submittingConnection}
              onChange={(e) =>
                handleConnectionField(
                  "charge_amount",
                  e.target.value
                )
              }
            />

            <Form.Text className="text-muted">
              Optional. Keep 0 when no charge is
              required.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label
              style={{ fontWeight: 500 }}
            >
              Remarks
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Optional remarks"
              value={connectionForm.remarks}
              disabled={submittingConnection}
              onChange={(e) =>
                handleConnectionField(
                  "remarks",
                  e.target.value
                )
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={closeConnectionModal}
            disabled={submittingConnection}
          >
            Cancel
          </Button>

          <Button
            variant={
              connectionModal.actionType ===
              "disconnection"
                ? "danger"
                : "success"
            }
            onClick={submitConnectionRequest}
            disabled={submittingConnection}
          >
            {submittingConnection ? (
              <>
                <Spinner
                  size="sm"
                  animation="border"
                  className="me-2"
                />
                Submitting...
              </>
            ) : connectionModal.actionType ===
              "disconnection" ? (
              "Submit Disconnection"
            ) : (
              "Submit Reconnection"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
